import { g } from './genderCopy';
// Reviews V5 — the DEFAULT review card on every public surface (Founder order 30/09/26): pro pages, category/city listing cards
// (components/ReviewsList.tsx), homepage strip. Supersedes the ?reviews=2|3|4 previews of 29/09/26 — no flag, every pro.
//   • reviewer = FULL name incl. surname (he_reviews.reviewer_name_full_he, lib/reviewerFullNameHe.ts) — Founder 30/09/26 explicit
//     order, replaces the "first name + surname initial" display rule (memory experts-reviewer-surname-leak, V5 law). Where the data
//     has no surname we show what we have — never an invented one (reviewer_name carries diversify() fake surnames → first word only).
//   • NO reviewer city on a card (reviewer_city is an assigned value) → the review SOURCE (Google / Facebook icon + name) + dd/MM/yy.
//   • "(3)" after the name = the reviewer's own Google review count, with a tooltip (components/ReviewerCountTip.tsx); only when ≥ 2.
//   • the word is always "המלצות" (never "ביקורות" — deploy gate scripts/reviews-word-audit.mjs); the pro total = Google + Facebook.
// The old preview allowlist survives ONLY as the noindex door to an INACTIVE allowlisted pro (?reviews=… — e.g. 3934, waiting for a
// photo) so the Founder can look at it before activation; it no longer changes how reviews look.
export const REVIEWS_V2_PREVIEW_IDS: ReadonlySet<number> = new Set([1341, 3625, 3708, 2134, 3934, 7133, 7135]);

/** Preview version from `?reviews=`: "v2"/"2" … "v5"/"5" → n (any case), else 0. Pure — safe in client components. */
export function reviewsParamVersion(param: string | string[] | null | undefined): number {
  const v = ((Array.isArray(param) ? param[0] : param) || '').trim();
  const m = v.match(/^v?([2-5])$/i);
  return m ? Number(m[1]) : 0;
}
export function isReviewsV2Param(param: string | string[] | null | undefined): boolean {
  return reviewsParamVersion(param) !== 0;
}
/** ONLY the inactive-pro preview door (getPro allowInactive + noindex). Never a design switch any more. */
export function reviewsV2Active(proId: number, param: string | string[] | undefined): boolean {
  return isReviewsV2Param(param) && REVIEWS_V2_PREVIEW_IDS.has(proId);
}


export type ReviewSource = 'google' | 'facebook' | 'experts';
export const SOURCE_NAME: Record<ReviewSource, string> = { google: 'Google', facebook: 'Facebook', experts: 'experts.co.il' };
/** The pro-page card's source line — plain text, never a link. */
export const SOURCE_LABEL: Record<ReviewSource, string> = { google: 'המלצה מאומתת מ-Google', facebook: 'המלצה מאומתת מ-Facebook', experts: 'המלצה מאומתת על ידי המומחים' }; // experts = verified by us (Founder 25/10/26: a customer who answered our WhatsApp follow-up)
export const reviewSourceOf = (v: unknown): ReviewSource => (v === 'facebook' ? 'facebook' : v === 'experts' ? 'experts' : 'google');

/** The ONE public word for reviews (Founder 29-30/09/26). The argument is ignored — kept so old call sites compile. */
export const REVIEWS_WORD = 'המלצות';
export const reviewsWord = (_legacy?: boolean) => REVIEWS_WORD;

/** Label only when nothing readable is left (Founder 29/09/26: handle → closest name, business → business name). Gendered by
 *  he_reviews.reviewer_gender (lib/gender.ts, Founder 30/09/26): "לקוח מאומת" / "לקוחה מאומתת" / neutral "לקוח מאומת". */
export const NEUTRAL_REVIEWER_LABEL = 'לקוח מאומת';   // Founder 30/09: a nameless reviewer's NAME slot says "לקוח מאומת" (was "המלצה מאומתת") — ONE constant, every surface
const REVIEWER_LABEL = { m: 'לקוח מאומת', f: 'לקוחה מאומתת', u: NEUTRAL_REVIEWER_LABEL };
export const reviewerLabel = (gender?: string | null) => g(gender, REVIEWER_LABEL);
const isReviewerLabel = (s: string | null | undefined) => !!s && Object.values(REVIEWER_LABEL).includes(s);

const LATIN = /[A-Za-z\u0400-\u04FF\u0600-\u06FF]/; // bughunter 09-30: Cyrillic / Arabic are "foreign" exactly like Latin ("Ирина Макаревич" reached a Hebrew card)
const HEB = /[֐-׿]/;
/** Hebrew words only ("הובלות I.R moving" → "הובלות"); '' when no Hebrew word is left. */
function hebrewWordsOnly(s: string): string {
  return s.split(/\s+/).filter(w => HEB.test(w) && !LATIN.test(w)).join(' ').replace(/[()\-–|,]+$/g, '').trim();
}

export type ReviewNameFields = {
  reviewer_name_full_he?: string | null; reviewer_name_full_he_kind?: string | null;
  original_reviewer_name?: string | null; reviewer_name?: string | null; reviewer_gender?: string | null;
};
/** The V5 card name. Full Hebrew name when we have it; a Latin leftover (business "The Sultan's", unprocessed "Liat Phone") never
 *  reaches a Hebrew card — its Hebrew words, else the first name of our Hebrew short name (a real transliteration — its surname
 *  may be a cosmetic stand-in, so it is dropped), else the neutral label. */
export function reviewCardName(r: ReviewNameFields): string {
  const clean = (s: string | null | undefined) => (s || '').replace(/[‎‏‪-‮]/g, '').replace(/\s+/g, ' ').trim();
  const kind = r.reviewer_name_full_he_kind || '';
  const NEUTRAL = reviewerLabel(r.reviewer_gender);
  const full = clean(r.reviewer_name_full_he);
  const shortFirst = (() => { const w = clean(r.reviewer_name).split(' ')[0] || ''; return HEB.test(w) && !LATIN.test(w) && w !== 'לקוח' ? w : ''; })();
  // kind FIRST (bughunter 30/09, row 77986): a business/handle account never shows its words as a person's name —
  // "miya mor optics ניידת האופטיקה" used to return its Hebrew half "ניידת האופטיקה"
  if (kind === 'handle' || kind === 'business') return NEUTRAL;
  if (full) {
    if (!LATIN.test(full)) return full;
    const he = hebrewWordsOnly(full);
    if (he) return he;
    return shortFirst || NEUTRAL;
  }
  const orig = clean(r.original_reviewer_name);
  if (orig && !LATIN.test(orig)) return orig;           // Hebrew original not filled yet — the reviewer's own words
  return shortFirst || NEUTRAL;
}
/** @deprecated V2 name — kept for old call sites; same rules as reviewCardName. */
export function v2ReviewerName(fullHe: string | null | undefined, kind: string | null | undefined, original: string | null | undefined): string {
  return reviewCardName({ reviewer_name_full_he: fullHe, reviewer_name_full_he_kind: kind, original_reviewer_name: original });
}

/** "(3)" after the reviewer name + tooltip "ישראל כתב 3 המלצות ב-Google" (gendered; unknown → "3 המלצות ב-Google בחשבון של ישראל"). Only a known count ≥ 2 (never "(1)", never a guess;
 *  Facebook rows store NULL). `short` is digits only. */
export function reviewerCountLabel(n: number | null | undefined, source: ReviewSource = 'google', name?: string, gender?: string | null): { short: string; tip: string } | null {
  if (n == null || source !== 'google') return null;
  const c = Number(n);
  if (!Number.isFinite(c) || c < 1) return null;   // Founder 11/10: ALWAYS shown, a single review too
  const first = (name || '').split(' ')[0];
  const named = !!first && !isReviewerLabel(name);
  const what = c === 1 ? 'המלצה אחת ב-Google' : `${c} המלצות ב-Google`;
  const tip = g(gender, {
    m: `${named ? first : 'הממליץ'} כתב ${what}`,
    f: `${named ? first : 'הממליצה'} כתבה ${what}`,
    u: named ? `${what} בחשבון של ${first}` : `${what} מאותו חשבון`,
  });
  return { short: String(c), tip };
}

/** Pro id from a pro-page path ("/<cat>/<slug>-1341") — for chrome (footer/menu) that has no pro object. */
export function proIdFromPath(pathname: string | null | undefined): number | null {
  const m = decodeURIComponent(pathname || '').match(/-(\d+)\/?$/);
  return m ? Number(m[1]) : null;
}

/** Columns every review card needs (append to a he_reviews SELECT). */
export const REVIEW_CARD_COLUMNS_SQL = 'reviewer_gender, reviewer_name_full_he, reviewer_name_full_he_kind, original_reviewer_name, reviewer_google_review_count, review_source';
/** Public Facebook recommendations of pro `p` — added to p.review_count (Google's total) = the ONE total everywhere (V5). */
export const PUBLIC_FB_COUNT_SQL = "(SELECT COUNT(*) FROM he_reviews fbr WHERE fbr.professional_id = p.id AND fbr.review_source = 'facebook'"
  + ' AND (fbr.source_recommends IS NULL OR fbr.source_recommends = 1) AND fbr.needs_translation = 0 AND fbr.name_needs_translation = 0'
  + ' AND fbr.processed_at IS NOT NULL AND fbr.hold_reason IS NULL)';
